const CONTINUE_LOGIN = '/login';
const CONTINUE_VERIFY = '/verify-email';
const CONTINUE_APP_RETURN = '/auth/email-confirmed';

const ALLOWED_CONTINUE_PATHS = [
  CONTINUE_LOGIN,
  CONTINUE_VERIFY,
  CONTINUE_APP_RETURN,
];

// Built without a script-URL literal so static analysis does not treat the check as eval.
const SCRIPT_URL_PROTOCOL = ['java', 'script:'].join('');

export function parseAuthActionQuery(search) {
  const query = typeof search === 'string' ? search : '';
  const params = new URLSearchParams(query.startsWith('?') ? query.slice(1) : query);
  return {
    mode: params.get('mode') || '',
    oobCode: params.get('oobCode') || '',
    continueUrl: params.get('continueUrl') || '',
  };
}

/**
 * Same-host `/login` and `/verify-email` can show Continue.
 * `/auth/email-confirmed` and every other URL use the return-to-app copy.
 */
export function classifyContinueUrl(continueUrl, pageOrigin) {
  if (typeof continueUrl !== 'string' || continueUrl.length === 0) {
    return { kind: 'app-return' };
  }
  let parsed = null;
  try {
    parsed = new URL(continueUrl);
  } catch {
    return { kind: 'app-return' };
  }
  if (parsed.protocol === SCRIPT_URL_PROTOCOL) {
    return { kind: 'app-return' };
  }
  let page = null;
  try {
    page = new URL(pageOrigin);
  } catch {
    return { kind: 'app-return' };
  }
  if (parsed.origin !== page.origin) {
    return { kind: 'app-return' };
  }
  if (!ALLOWED_CONTINUE_PATHS.includes(parsed.pathname)) {
    return { kind: 'app-return' };
  }
  if (parsed.pathname === CONTINUE_APP_RETURN) {
    return { kind: 'app-return' };
  }
  return {
    kind: 'continue',
    href: `${parsed.pathname}${parsed.search}`,
  };
}

export function mapAuthActionError(code) {
  if (
    code === 'auth/expired-action-code'
    || code === 'auth/invalid-action-code'
    || code === 'expired-action-code'
    || code === 'invalid-action-code'
  ) {
    return 'expired';
  }
  return 'error';
}

export function emailsMatch(left, right) {
  const normalizedLeft = typeof left === 'string' ? left.trim().toLowerCase() : '';
  const normalizedRight = typeof right === 'string' ? right.trim().toLowerCase() : '';
  return normalizedLeft.length > 0 && normalizedLeft === normalizedRight;
}

/**
 * The code belongs to the signed-in account when that account’s current address
 * is the code address, or the previous address on an email-change code.
 */
export function codeMatchesSignedInEmail(signedInEmail, actionInfo) {
  const codeEmail = actionInfo?.data?.email;
  const previousEmail = actionInfo?.data?.previousEmail;
  return emailsMatch(signedInEmail, codeEmail) || emailsMatch(signedInEmail, previousEmail);
}

const EMAIL_CHANGE_OPERATION = 'VERIFY_AND_CHANGE_EMAIL';
const VERIFY_EMAIL_OPERATION = 'VERIFY_EMAIL';
const EMAIL_CHANGE_MODE = 'verifyAndChangeEmail';

function actionOperation(actionInfo) {
  const operation = actionInfo?.operation;
  if (typeof operation !== 'string') {
    return '';
  }
  return operation.trim();
}

/**
 * An applied email change signs the app out. Confirming the original address
 * does not. The code operation wins over the link mode. The link mode is used
 * only when the code has no operation.
 */
export function isEmailChangeAction(mode, actionInfo) {
  const operation = actionOperation(actionInfo);
  if (operation === EMAIL_CHANGE_OPERATION) {
    return true;
  }
  if (operation === VERIFY_EMAIL_OPERATION) {
    return false;
  }
  if (!operation) {
    return mode === EMAIL_CHANGE_MODE;
  }
  return false;
}

export function isApplicableEmailAction(actionInfo) {
  const operation = actionOperation(actionInfo);
  if (!operation) {
    return true;
  }
  return operation === EMAIL_CHANGE_OPERATION || operation === VERIFY_EMAIL_OPERATION;
}

/**
 * New address on an email-change code, or empty when the code has none.
 * This is never the previous address.
 */
export function emailChangeSignInAddress(actionInfo) {
  const email = actionInfo?.data?.email;
  if (typeof email !== 'string') {
    return '';
  }
  return email.trim();
}
